import { useUserSession } from "@/stores/userSession";
import { useDeploymentSettingsStore } from "@/stores/deploymentSettings";
import { getRouteModule } from "@/utils/maintenanceMode";
import { canUserAccessModuleFully } from "@/utils/moduleAccess";
import {
  HOME_ROUTE,
  redirectToAccessibleRoute,
  resolvePostLoginRoute,
} from "@/utils/navigationAccess";
import { redirectToOldCapmagicForMaintenance } from "@/utils/oldCapmagicRedirect";
import { useModuleAccessApi } from "@/api/moduleAccess/useModuleAccessApi";
import { type App, type Plugin } from "vue";
import type { RouteLocationNormalized } from "vue-router";
import type { AppModule } from "@/config/maintenanceModules";
import config from "@/config";
import { isPublicRoute } from "@/utils/publicRoutes";


// Routes/modules whose meta.allowedUsers excludes the regular "user" role
// are admin tools (deployment settings, login history, ddi-library, etc.) —
// a non-admin bounced from one of these should land on the home page, not
// on whatever other module they happen to have access to.
function isAdminOnlyRoute(to: RouteLocationNormalized): boolean {
  const allowedUsers = to.meta?.allowedUsers;
  return (
    Array.isArray(allowedUsers) && !allowedUsers.includes(config.userType.user)
  );
}

// Self-reports "which module is the user actually using" for the Login
// History "modules used this session" column. Deduped on module change so
// it's one call per module visit, not per navigation.
let lastReportedModule: AppModule | undefined;
function reportModuleAccess(module: AppModule | undefined) {
  if (!module || module === lastReportedModule) {
    return;
  }
  lastReportedModule = module;
  useModuleAccessApi().ping(module).catch(() => {});
}

// A failing deployment-settings request must not block navigation: on error the
// store keeps its current (default / previously loaded) settings, i.e. the same
// state as when the API answers without data.
async function fetchDeploymentSettingsSafely(
  deploymentStore: ReturnType<typeof useDeploymentSettingsStore>
) {
  try {
    await deploymentStore.fetchSettings();
  } catch (error) {
    console.error("Failed to load deployment settings", error);
  }
}

export const routerGuardPlugin: Plugin = {
  install: (app: App) => {
    const userSession = useUserSession();

    app.config.globalProperties.$router.beforeEach(async (to, from, next) => {
      const userType = Number(userSession.user.userType);
      const email = userSession.user.email;

      if (!isPublicRoute(to.path) && userSession.token) {
        const deploymentStore = useDeploymentSettingsStore();
        await fetchDeploymentSettingsSafely(deploymentStore);

        const module = getRouteModule(to);
      //   //TODO: Remove old CapMagic maintenance redirection once all modules are fully migrated.
      //   if (
      //     module &&
      //     deploymentStore.shouldShowMaintenanceForUser(module, email) &&
      //     redirectToOldCapmagicForMaintenance(module, userSession.token)
      //   ) {
      //     next(false);
      //     return;
      //   }

        if (
          module &&
          !canUserAccessModuleFully(module, userType, email, deploymentStore)
        ) {
          if (isAdminOnlyRoute(to)) {
            next(HOME_ROUTE);
            return;
          }
          redirectToAccessibleRoute(to, next, userType, email, deploymentStore);
          return;
        }

        reportModuleAccess(module);
      }

      if (!isPublicRoute(to.path) && !userSession.token) {
        next("/login");
      } else if (to.path === "/login" && userSession.token) {
        next(resolvePostLoginRoute());
      } else if (
        to.path === "/admin" &&
        userSession.token &&
        !userSession.isSalesForceLoggedIn
      ) {
        next(HOME_ROUTE);
      } else {
        if (
          to.meta?.allowedUsers &&
          Array.isArray(to.meta.allowedUsers) &&
          !to.meta.allowedUsers.includes(userType)
        ) {
          if (isAdminOnlyRoute(to)) {
            next(HOME_ROUTE);
            return;
          }
          const deploymentStore = useDeploymentSettingsStore();
          await fetchDeploymentSettingsSafely(deploymentStore);
          redirectToAccessibleRoute(to, next, userType, email, deploymentStore);
        } else {
          next();
        }
      }
    });
  },
};
